import './HSM.css';
import React, { useState, useEffect } from 'react';

type StateOwnershipProps = {
  editing: boolean;
  ownership: string;
  humanMode: boolean;
  setOwnership: (x: string) => void;
};

const StateOwnership = ({
  editing,
  humanMode,
  ownership,
  setOwnership,
}: StateOwnershipProps) => {
  const hasOwnership = ownership !== '';
  const currentSelection = hasOwnership ? ownership : 'No ownership';

  const OwnershipDisplay = () => {
    const className = !ownership || humanMode ? 'invis' : '';
    return (
      <b>
        <p className={className}>{!ownership ? '.' : ownership}</p>
      </b>
    );
  };

  const OwnershipEdit = () => {
    function handleChange(e) {
      setOwnership(e.target.value);
    }

    if (humanMode) {
      return null;
    }

    return (
      <select
        className='ownership-select'
        id='ownership-select'
        value={currentSelection}
        onChange={(e) => handleChange(e)}
      >
        <option value=''>No ownership</option>
        <option value='Caregiver'>Caregiver</option>
        <option value='Care recipient'>Care recipient</option>
        <option value='Robot'>Robot</option>
        <option value='Caregiver-Care recipient'>
          Caregiver-Care recipient
        </option>
        <option value='Caregiver-Robot'>Caregiver-Robot</option>
        <option value='Care recipient-Robot'>Care recipient-Robot</option>
        <option value='Caregiver-Care recipient-Robot'>
          Caregiver-Care recipient-Robot
        </option>
      </select>
    );
  };

  return (
    <div className='ownership'>
      {editing && <OwnershipEdit />}
      {!editing && <OwnershipDisplay />}
    </div>
  );
};

type StateToolsProps = {
  onClickComments: () => void;
  onClickPapers: () => void;
  onClickTrash: () => void;
  onClickDeeper: () => void;
  onClickConditions: () => void;
  type: string;
  discussionButtons: boolean;
  editing: boolean;
  setCursorAuto: () => void;
  setCursorPointer: () => void;
  isHumanMode: boolean;
  invis: boolean;
};

const StateTools = ({
  onClickComments,
  onClickPapers,
  onClickTrash,
  onClickDeeper,
  onClickConditions,
  type,
  discussionButtons,
  editing, // quick editing
  setCursorAuto,
  setCursorPointer,
  isHumanMode,
  invis,
}: StateToolsProps) => {
  const CommentIcon = () => {
    return (
      <span onMouseEnter={setCursorPointer} onMouseLeave={setCursorAuto}>
        <svg
          onClick={onClickComments}
          xmlns='http://www.w3.org/2000/svg'
          viewBox='-2 -2.5 24 24'
          width='24'
          fill='currentColor'
        >
          <path d='M9.378 12H17a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1 1 1 0 0 1 1 1v3.013L9.378 12zM3 0h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-6.958l-6.444 4.808A1 1 0 0 1 2 18.006V14a2 2 0 0 1-2-2V3a3 3 0 0 1 3-3z'></path>
        </svg>
      </span>
    );
  };

  const PaperIcon = () => {
    return (
      <span onMouseEnter={setCursorPointer} onMouseLeave={setCursorAuto}>
        <svg
          onClick={onClickPapers}
          xmlns='http://www.w3.org/2000/svg'
          viewBox='-4 -2 24 24'
          width='24'
          fill='currentColor'
        >
          <path d='M3 0h10a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3zm0 2a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H3zm2 1h6a1 1 0 0 1 0 2H5a1 1 0 1 1 0-2zm0 12h2a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2zm0-4h6a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2zm0-4h6a1 1 0 0 1 0 2H5a1 1 0 1 1 0-2z'></path>
        </svg>
      </span>
    );
  };

  const TrashIcon = ({ onClick }) => {
    return (
      <span onMouseEnter={setCursorPointer} onMouseLeave={setCursorAuto}>
        <svg
          onClick={onClick}
          xmlns='http://www.w3.org/2000/svg'
          viewBox='-3 -2 24 24'
          width='24'
          fill='currentColor'
        >
          <path d='M6 2V1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h4a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-.133l-.68 10.2a3 3 0 0 1-2.993 2.8H5.826a3 3 0 0 1-2.993-2.796L2.137 7H2a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4zm10 2H2v1h14V4zM4.141 7l.687 10.068a1 1 0 0 0 .998.932h6.368a1 1 0 0 0 .998-.934L13.862 7h-9.72zM7 8a1 1 0 0 1 1 1v7a1 1 0 0 1-2 0V9a1 1 0 0 1 1-1zm4 0a1 1 0 0 1 1 1v7a1 1 0 0 1-2 0V9a1 1 0 0 1 1-1z'></path>
        </svg>
      </span>
    );
  };

  const DeeperIcon = ({ onClick }) => {
    return (
      <svg
        onClick={onClick}
        xmlns='http://www.w3.org/2000/svg'
        viewBox='-5 -4.5 24 24'
        width='24'
        fill='currentColor'
      >
        <path d='M6 4.071l-3.95 3.95A1 1 0 0 1 .636 6.607L6.293.95a.997.997 0 0 1 1.414 0l5.657 5.657A1 1 0 0 1 11.95 8.02L8 4.07v9.586a1 1 0 1 1-2 0V4.07z'></path>
      </svg>
    );
  };

  const ConditionsIcon = ({ onClick }) => {
    return (
      <svg
        onClick={onClick}
        xmlns='http://www.w3.org/2000/svg'
        viewBox='-2 -2 24 24'
        width='24'
        fill='currentColor'
      >
        <path d='M6 0h8a6 6 0 0 1 6 6v8a6 6 0 0 1-6 6H6a6 6 0 0 1-6-6V6a6 6 0 0 1 6-6zm0 2a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4V6a4 4 0 0 0-4-4H6zm6 7h3a1 1 0 0 1 0 2h-3a1 1 0 0 1 0-2zm-2 4h5a1 1 0 0 1 0 2h-5a1 1 0 0 1 0-2zm0-8h5a1 1 0 0 1 0 2h-5a1 1 0 1 1 0-2zm-4.172 5.243L7.95 8.12a1 1 0 1 1 1.414 1.415l-2.828 2.828a1 1 0 0 1-1.415 0L3.707 10.95a1 1 0 0 1 1.414-1.414l.707.707z'></path>
      </svg>
    );
  };

  let buttons = [];

  if ((!isHumanMode && type != 'skill') || (isHumanMode && type != 'task')) {
    // since skills are the lowest layer of abstraction, we cannot go deeper
    buttons.push(<DeeperIcon onClick={onClickDeeper} />);
  }

  if (!isHumanMode && type === 'task') {
    buttons.push(<ConditionsIcon onClick={onClickConditions} />);
  }

  if (editing) {
    // push the edit-mode buttons to the toolbar
    buttons.push(<TrashIcon onClick={onClickTrash} />);
  } else {
    // push the view-mode buttons to the toolbar
    if (discussionButtons) {
      buttons.push(<CommentIcon />);
      buttons.push(<PaperIcon />);
    }
  }

  const invisible = buttons.length === 0;
  if (invisible) {
    buttons.push(<CommentIcon />);
  }

  const className = invis || invisible ? 'tools invis' : 'tools';

  return <div className={className}>{buttons}</div>;
};

export type CommentProps = {
  author: string;
  occupation: string;
  text: string;
};

export type PaperProps = {
  title: string;
  author: string;
  url: string;
  modelInfo: string;
};

type StateBoxProps = {
  id: number; // unique ID. (unique across entire HSM activity object)
  uid: string;
  template: string;
  author: number; // author ID
  type: string;
  text: string;
  preconditions: string;
  setPreconditions: (x: string) => void;
  postconditions: string;
  setPostconditions: (x: string) => void;
  setText: (x: string) => void;
  ownership: string;
  setOwnership: (x: string) => void;
  discussionButtons: boolean;
  hideTools?: boolean;
  viewOnlyMode: boolean;
  editing: boolean;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  deleteSelf: () => void;
  children: StateBoxProps[];
  comments: CommentProps[];
  showComments: () => void;
  papers: PaperProps[];
  showPapers: () => void;
  onClick: () => void;
  setCursorPointer: () => void;
  setCursorAuto: () => void;
  humanMode: boolean;
};

export const StateBox = ({
  id,
  uid,
  template,
  author,
  type,
  text,
  preconditions,
  setPreconditions,
  postconditions,
  setPostconditions,
  setText,
  ownership,
  setOwnership,
  discussionButtons,
  hideTools = false,
  viewOnlyMode,
  editing, // whether the renderer is in edit mode or view mode
  setEditing,
  deleteSelf,
  children,
  comments,
  showComments,
  showPapers,
  papers,
  onClick,
  setCursorPointer,
  setCursorAuto,
  humanMode,
}: StateBoxProps) => {
  const [showingConditions, setShowingConditions] = useState(false);
  const onClickTrash = () => {
    deleteSelf();
  };

  const onClickConditions = () => {
    setShowingConditions((prev) => !prev);
  };

  const emptyOnClick = () => {};

  let toolsProps: StateToolsProps = {
    onClickComments: showComments,
    onClickPapers: showPapers,
    onClickTrash: onClickTrash,
    onClickDeeper: onClick,
    onClickConditions,
    type: type,
    discussionButtons: discussionButtons,
    editing: editing,
    setCursorAuto: setCursorAuto,
    setCursorPointer: setCursorPointer,
    isHumanMode: humanMode,
    invis: hideTools,
  };

  let content;

  if (editing) {
    let textAreaProps = {
      value: text,
      onChange: (e) => {
        setText(e.target.value);
      },
      className: 'HSM-state-text',
      placeholder: type,
    };
    content = <textarea {...textAreaProps} />;
  } else {
    let spanProps = {
      className: 'HSM-state-text',
      onClick: onClick,
      onMouseEnter: setCursorPointer,
      onMouseLeave: setCursorAuto,
    };
    content = (
      <span {...spanProps}>
        <p>{text}</p>
      </span>
    );
  }

  let stateClass;
  let levelClass;

  switch (type) {
    case 'activity':
      stateClass = 'HSM-state cactivity';
      levelClass = 'level0';
      break;
    case 'composite task':
      stateClass = 'HSM-state comptask';
      levelClass = 'level1';
      break;
    case 'task':
      stateClass = 'HSM-state task';
      levelClass = 'level2';
      break;
    case 'composite skill':
      stateClass = 'HSM-state compskill';
      levelClass = 'level3';
      break;
    case 'skill':
      stateClass = 'HSM-state skill';
      levelClass = 'level4';
      break;
  }

  if (onClick && onClick.toString() !== emptyOnClick.toString()) {
    // the current layer should not have padding, otherwise it messes up table
    levelClass = '';
  }

  return (
    <div className={'HSM-Wrapper ' + levelClass}>
      <div className={stateClass}>
        {
          <StateOwnership
            editing={editing}
            ownership={ownership}
            humanMode={humanMode}
            setOwnership={setOwnership}
          />
        }
        {content}
        {!viewOnlyMode && <StateTools {...toolsProps} />}
      </div>
      {type === 'task' && showingConditions && (
        <ConditionsBox
          preconditions={preconditions}
          setPreconditions={setPreconditions}
          postconditions={postconditions}
          setPostconditions={setPostconditions}
          editing={editing}
        />
      )}
    </div>
  );
};

export type ConditionsBoxProps = {
  preconditions: string;
  setPreconditions: (x: string) => void;
  postconditions: string;
  setPostconditions: (x: string) => void;
  editing: boolean;
};

const ConditionsBox = ({
  preconditions,
  setPreconditions,
  postconditions,
  setPostconditions,
  editing,
}: ConditionsBoxProps) => {
  if (!editing) {
    const preText =
      !preconditions || preconditions === '' ? 'None declared' : preconditions;
    const postText =
      !postconditions || postconditions === ''
        ? 'None declared'
        : postconditions;
    return (
      <div className='HSM-Conditions animatable'>
        <div className='conditions-text'>
          <h6>
            <b>Preconditions</b>
          </h6>

          <p className='precond-text border-bottom border-dark'>{preText}</p>

          <h6>
            <b>Postconditions</b>
          </h6>

          <p>{postText}</p>
        </div>
      </div>
    );
  } else {
    return (
      <div className='HSM-Conditions animatable'>
        <div className='conditions-text'>
          <h6>
            <b>Preconditions</b>
          </h6>

          <textarea
            onChange={(e) => setPreconditions(e.target.value)}
            value={preconditions}
            className='conditionsTextArea'
          />

          <h6>
            <b>Postconditions</b>
          </h6>

          <textarea
            onChange={(e) => setPostconditions(e.target.value)}
            value={postconditions}
            className='conditionsTextArea'
          />
        </div>
      </div>
    );
  }
};

export type { StateBoxProps };
export default StateBox;
